import React, { useEffect, useMemo, useState, Component } from 'react';
/**
 * FeedbackSplash — Full-screen celebration/encouragement overlay.
 *
 * Shows after answering a question: confetti + Djay for correct,
 * warm floating particles + encouragement for incorrect.
 * Auto-advances after a configurable duration, tap to skip.
 *
 * Optimized s64: particle data + display config extracted to feedbackSplashData.ts
 * (667→~420 lines). Co-located in practice/ domain (CL-1 s64).
 */
import { motion, useReducedMotion } from 'framer-motion';
import { Star, Sparkles } from 'lucide-react';
import { DjayAvatar, useDjaySelection, getDjaySplashMessage, type DjayTier, type DjaySkin } from '../../../mascot';
import { soundManager } from '../../../utils/soundManager';
import { CONFETTI_PARTICLES, EXTRA_CONFETTI, WARM_PARTICLES, buildSplashDisplayConfig, resolveSplashPriority, type ConfettiParticle, type WarmParticle } from './feedbackSplashData';
// ── Types ────────────────────────────────────────────────────
interface FeedbackSplashProps {
  isCorrect: boolean;
  xpGained: number;
  streak?: number;
  onComplete: () => void;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
  streakMilestone?: {
    count: number;
    message: string;
  } | null;
  dailyGoalReached?: boolean;
  tierEvolution?: {
    from: DjayTier;
    to: DjayTier;
  } | null;
  skinUnlock?: {
    skin: DjaySkin;
    description: string;
  } | null;
}
// ── Shared Sub-components ────────────────────────────────────
function ConfettiLayer({
  particles,
  reduced
}: {
  particles: ConfettiParticle[];
  reduced: boolean | null;
}) {
  if (reduced) return null;
  return <>
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.color} ${p.size} pointer-events-none opacity-80`} style={{
      left: `${p.startX}%`,
      top: `${p.startY}%`
    }} initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      x: `${(p.endX - p.startX) * 2.5}vw`,
      y: `${p.endY - p.startY}vh`,
      scale: [0, 1.2, 1, 0.6],
      opacity: [0, 0.9, 0.9, 0],
      rotate: p.rotation
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      ease: 'easeOut'
    }} data-id="element-6634" />)}
    </>;
}
function WarmParticleLayer({
  particles,
  reduced
}: {
  particles: WarmParticle[];
  reduced: boolean | null;
}) {
  if (reduced) return null;
  return <>
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.color} ${p.size} pointer-events-none opacity-50`} style={{
      left: `${p.startX}%`,
      bottom: '0%'
    }} animate={{
      y: [`${p.startY}vh`, `${p.endY}vh`],
      x: [0, (Math.random() - 0.5) * 40],
      opacity: [0, 0.5, 0.5, 0]
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-6635" />)}
    </>;
}
// ── Hooks ────────────────────────────────────────────────────
function useXpCounter(target: number) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (target === 0) return;
    const steps = 15;
    const increment = target / steps;
    const interval = 500 / steps;
    let current = 0;
    const id = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplay(target);
        clearInterval(id);
      } else {
        setDisplay(Math.floor(current));
      }
    }, interval);
    return () => clearInterval(id);
  }, [target]);
  return display;
}
// ── Component ────────────────────────────────────────────────
export function FeedbackSplash({
  isCorrect,
  xpGained,
  streak = 0,
  onComplete,
  levelUp,
  streakMilestone,
  dailyGoalReached,
  tierEvolution,
  skinUnlock
}: FeedbackSplashProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const shouldReduceMotion = useReducedMotion();
  const xpDisplay = useXpCounter(xpGained);
  const splashMsg = useMemo(() => getDjaySplashMessage(isCorrect, streak), [isCorrect, streak]);
  // Play sound feedback on mount
  useEffect(() => {
    if (levelUp) {
      soundManager.playLevelUp();
    } else if (dailyGoalReached) {
      soundManager.playGoalReached();
    } else if (streakMilestone) {
      soundManager.playStreak();
    } else if (isCorrect) {
      soundManager.playCorrect();
    } else {
      soundManager.playWrong();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const config = useMemo(() => buildSplashDisplayConfig({
    isCorrect,
    streak,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
    splashMsg
  }), [isCorrect, streak, levelUp, tierEvolution, skinUnlock, dailyGoalReached, streakMilestone, splashMsg]);
  const priority = useMemo(() => resolveSplashPriority({
    isCorrect,
    streak,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
    splashMsg
  }), [isCorrect, streak, levelUp, tierEvolution, skinUnlock, dailyGoalReached, streakMilestone, splashMsg]);
  const {
    mainMessage,
    subMessage,
    emotion,
    duration,
    gradient,
    showExtraConfetti
  } = config;
  const noMotion = !!shouldReduceMotion;
  // Auto-advance timer
  useEffect(() => {
    const timer = setTimeout(onComplete, duration);
    return () => clearTimeout(timer);
  }, [duration, onComplete]);
  // ── Correct Splash ──
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25
    }} onClick={onComplete} className="min-h-[calc(100vh-80px)] w-full flex flex-col items-center justify-center overflow-hidden cursor-pointer relative rounded-3xl" style={{
      background: gradient
    }} data-id="element-6636">
        {/* Radial glow */}
        {!noMotion && <motion.div className="absolute w-[300px] h-[300px] rounded-full pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 70%)'
      }} initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1.3,
        opacity: 1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-6637" />}

        <ConfettiLayer particles={CONFETTI_PARTICLES} reduced={shouldReduceMotion} data-id="element-6638" />
        {showExtraConfetti && <ConfettiLayer particles={EXTRA_CONFETTI} reduced={shouldReduceMotion} data-id="element-6639" />}

        {/* Djay Avatar */}
        <motion.div initial={{
        scale: noMotion ? 1 : 0,
        rotate: noMotion ? 0 : -15,
        y: noMotion ? 0 : 40
      }} animate={{
        scale: 1,
        rotate: 0,
        y: noMotion ? 0 : [0, -8, 0, -5, 0]
      }} transition={{
        scale: {
          type: 'spring',
          stiffness: 350,
          damping: 18,
          delay: noMotion ? 0 : 0.1
        },
        rotate: {
          type: 'spring',
          stiffness: 350,
          damping: 18,
          delay: noMotion ? 0 : 0.1
        },
        y: {
          delay: noMotion ? 0 : 0.4,
          duration: noMotion ? 0 : 1.2,
          repeat: noMotion ? 0 : 1,
          ease: 'easeInOut'
        }
      }} className="relative z-10 mb-4" data-id="element-6640">
          <DjayAvatar size="hero" emotion={emotion} variant={selectedVariant} animate={!noMotion} data-id="element-6641" />
        </motion.div>

        {/* Main text */}
        <motion.h1 initial={{
        scale: noMotion ? 1 : 0.7,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 15
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 22,
        delay: noMotion ? 0 : 0.2
      }} className="text-3xl sm:text-4xl font-black text-amber-900 mb-4 tracking-tight text-center px-6" style={{
        textShadow: '0 2px 8px rgba(217, 119, 6, 0.15)'
      }} data-id="element-6642">
          {mainMessage}
        </motion.h1>

        {subMessage && <motion.p initial={{
        scale: noMotion ? 1 : 0.8,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 10
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 22,
        delay: noMotion ? 0 : 0.3
      }} className="text-xl sm:text-2xl font-bold text-amber-700 mb-6 text-center px-6" data-id="element-6643">
            {subMessage}
          </motion.p>}

        {/* Badges */}
        <motion.div initial={{
        scale: noMotion ? 1 : 0.7,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 8
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        delay: noMotion ? 0 : 0.35,
        type: 'spring',
        stiffness: 280
      }} className="flex flex-col items-center gap-3" data-id="element-6644">
          <div className="flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/80 backdrop-blur-sm border border-amber-300/50 shadow-lg shadow-amber-200/30" data-id="element-6645">
            <motion.div animate={{
            rotate: noMotion ? 0 : [0, 12, -12, 0],
            scale: noMotion ? 1 : [1, 1.15, 1]
          }} transition={{
            duration: noMotion ? 0 : 0.5,
            delay: noMotion ? 0 : 0.5
          }} data-id="element-6646">
              <Star className="w-5 h-5 fill-amber-500 text-amber-500" data-id="element-6647" />
            </motion.div>
            <span className="text-xl font-black text-amber-800" data-id="element-6648">
              +{xpDisplay} XP
            </span>
          </div>

          <div className="flex gap-2" data-id="element-6649">
            {priority === 'levelUp' && streakMilestone && <div className="px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-xs font-bold shadow-sm" data-id="element-6650">
                🔥 {streakMilestone.count} op rij
              </div>}
            {priority === 'levelUp' && dailyGoalReached && <div className="px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-bold shadow-sm" data-id="element-6651">
                ✅ Dagelijks doel
              </div>}
          </div>
        </motion.div>
      </motion.div>;
  }
  // ── Incorrect Splash ──
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} onClick={onComplete} className="min-h-[calc(100vh-80px)] w-full flex flex-col items-center justify-center overflow-hidden cursor-pointer relative rounded-3xl" style={{
    background: gradient
  }} data-id="element-6652">
      <WarmParticleLayer particles={WARM_PARTICLES} reduced={shouldReduceMotion} data-id="element-6653" />

      <motion.div initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 25,
      scale: noMotion ? 1 : 0.9
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1,
      rotate: noMotion ? 0 : [0, -3, 3, -2, 0]
    }} transition={{
      opacity: {
        duration: noMotion ? 0 : 0.4,
        ease: 'easeOut',
        delay: noMotion ? 0 : 0.1
      },
      y: {
        duration: noMotion ? 0 : 0.4,
        ease: 'easeOut',
        delay: noMotion ? 0 : 0.1
      },
      scale: {
        duration: noMotion ? 0 : 0.4,
        ease: 'easeOut',
        delay: noMotion ? 0 : 0.1
      },
      rotate: {
        delay: noMotion ? 0 : 0.5,
        duration: noMotion ? 0 : 0.6,
        ease: 'easeInOut'
      }
    }} className="relative z-10 mb-4" data-id="element-6654">
        <DjayAvatar size="hero" emotion={emotion} variant={selectedVariant} animate={!noMotion} data-id="element-6655" />
      </motion.div>

      <motion.h1 initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 15
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.2,
      duration: noMotion ? 0 : 0.35
    }} className="text-3xl sm:text-4xl font-black text-slate-800 mb-2 tracking-tight text-center px-6" data-id="element-6656">
        {mainMessage}
      </motion.h1>

      <motion.p initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.3,
      duration: noMotion ? 0 : 0.35
    }} className="text-lg sm:text-xl text-slate-600 font-semibold mb-5 text-center px-6" data-id="element-6657">
        {subMessage}
      </motion.p>

      <motion.div initial={{
      scale: noMotion ? 1 : 0.7,
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 8
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.4,
      type: 'spring',
      stiffness: 280
    }} className="flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 border border-orange-200 shadow-md" data-id="element-6658">
        <Sparkles className="w-4 h-4 fill-orange-400 text-orange-400" data-id="element-6659" />
        <span className="text-base font-bold text-orange-700" data-id="element-6660">
          +{xpDisplay} XP
        </span>
        <span className="text-xs font-semibold text-orange-500 uppercase tracking-wide" data-id="element-6661">
          voor de inzet
        </span>
      </motion.div>
    </motion.div>;
}